import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import './LogoCarousel.css';

const logos = [
  { name: 'ai动画制作', icon: '/sunny.svg' },
  { name: 'ai视频剪辑', icon: '/night-sunny.svg' },
  { name: '豆包AI去水印', icon: '/mask.svg' },
];

type LogoCarouselProps = {
  activeIndex?: number;
  onSelect?: (index: number) => void;
  navigateOnSelect?: boolean;
  persistKey?: string;
  animationDurationSec?: number;
};

const LogoCarousel: React.FC<LogoCarouselProps> = ({
  activeIndex: activeIndexProp,
  onSelect,
  navigateOnSelect = true,
  persistKey,
  animationDurationSec = 18,
}) => {
  const [activeIndexState, setActiveIndexState] = useState(0);
  const navigate = useNavigate();
  const activeIndex = activeIndexProp ?? activeIndexState;

  const trackStyle = useMemo(() => {
    if (!persistKey) return undefined;
    const storageKey = `logoCarouselStart:${persistKey}`;
    let start = Number(localStorage.getItem(storageKey) || '0');
    if (!Number.isFinite(start) || start <= 0) {
      start = Date.now();
      localStorage.setItem(storageKey, String(start));
    }
    const elapsedSec = ((Date.now() - start) / 1000) % animationDurationSec;
    return {
      animationDuration: `${animationDurationSec}s`,
      animationDelay: `-${elapsedSec}s`,
    } as React.CSSProperties;
  }, [persistKey, animationDurationSec]);

  // 复制一份，共 6 个，动画移动 -50% 刚好等于原始 3 个的总宽度，无缝衔接
  const displayLogos = useMemo(() => [...logos, ...logos], []);

  return (
    <Box className="logo-carousel-container">
      <Box className="logo-carousel-viewport">
        <Box className="logo-carousel-track" style={trackStyle}>
          {displayLogos.map((logo, index) => {
            const originalIndex = index % logos.length;
            const isActive = originalIndex === activeIndex;
            return (
              <Box
                key={index}
                className={`logo-item ${isActive ? 'active' : ''}`}
                onClick={() => {
                  if (activeIndexProp === undefined) setActiveIndexState(originalIndex);
                  onSelect?.(originalIndex);
                  if (navigateOnSelect) {
                    if (originalIndex === 0) navigate('/ai-animation');
                    if (originalIndex === 1) navigate('/ai-video-edit');
                    if (originalIndex === 2) navigate('/news');
                  }
                }}
              >
                {logo.icon && (
                  <img
                    src={logo.icon}
                    alt=""
                    className="logo-icon"
                    style={{ opacity: isActive ? 1 : 0.6 }}
                  />
                )}
                <Typography
                  variant="h6"
                  sx={{
                    color: isActive ? '#ed6c02' : '#64748b',
                    fontWeight: 600,
                    opacity: isActive ? 1 : 0.6,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {logo.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default LogoCarousel;
